<script setup lang="ts">
/**
 * Toast Container Component
 *
 * Renders toast notifications from the toastStore.
 * Should be placed once in App.vue or main layout.
 *
 * PHP REFERENCE:
 * - Matches /js/toast.js behavior
 * - Shows in bottom-right corner
 * - Auto-dismisses after duration
 * - Click to dismiss early
 */
import { useToastStore } from '@/stores/toastStore';
const toastStore = useToastStore();

function getIcon(type: string): string {
  switch (type) {
    case 'success':
      return 'check_circle';
    case 'error':
      return 'error';
    case 'warning':
      return 'warning';
    case 'info':
    default:
      return 'info';
  }
}
</script>

<template>
  <Teleport to="body">
    <div class="toast-container" aria-live="polite" aria-atomic="true">
      <TransitionGroup name="toast">
        <div
          v-for="toast in toastStore.toasts"
          :key="toast.id"
          :class="['toast', `toast-${toast.type}`]"
          data-alert-ignore="toast"
          role="alert"
          @click="toastStore.removeToast(toast.id)"
          @mouseenter="toastStore.pauseToast(toast.id)"
          @mouseleave="toastStore.resumeToast(toast.id)"
        >
          <bb-icon :name="getIcon(toast.type)" class="toast-icon" decorative></bb-icon>
          <span class="toast-message">{{ toast.message }}</span>
          <button
            class="toast-close"
            aria-label="Dismiss"
            @click.stop="toastStore.removeToast(toast.id)"
          >
            <bb-icon name="close" decorative></bb-icon>
          </button>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<style scoped>
.toast-container {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 400px;
}

.toast {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: var(--radius-default);
  box-shadow: var(--shadow-lg);
  cursor: pointer;
  min-width: 300px;
}

.toast-success {
  background: #E8F5E9;
  border: 1px solid #4CAF50;
  color: #1B5E20;
}

.toast-error {
  background: #FFEBEE;
  border: 1px solid #F44336;
  color: #B71C1C;
}

.toast-warning {
  background: #FFF8E1;
  border: 1px solid #FF9800;
  color: #E65100;
}

.toast-info {
  background: #E3F2FD;
  border: 1px solid #2196F3;
  color: #0D47A1;
}

.toast-icon {
  flex-shrink: 0;
}

.toast-icon :deep(bb-icon) {
  font-size: 20px;
}

.toast-message {
  flex: 1;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  white-space: pre-line;
}

.toast-close {
  flex-shrink: 0;
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  opacity: 0.6;
  border-radius: var(--radius-sm);
}

.toast-close:hover {
  opacity: 1;
  background: rgba(0, 0, 0, 0.1);
}

.toast-close :deep(bb-icon) {
  font-size: 18px;
}

/* Transitions */
.toast-enter-active {
  animation: toast-in 0.3s ease-out;
}

.toast-leave-active {
  animation: toast-out 0.2s ease-in;
}

@keyframes toast-in {
  from {
    opacity: 0;
    transform: translateX(100%);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes toast-out {
  from {
    opacity: 1;
    transform: translateX(0);
  }
  to {
    opacity: 0;
    transform: translateX(100%);
  }
}

@media (max-width: 480px) {
  .toast-container {
    left: 16px;
    right: 16px;
    bottom: 16px;
  }

  .toast {
    min-width: unset;
  }
}
</style>
